import { createFileRoute, Link } from '@tanstack/react-router';
import { useDeposit, useSetDepositCleared, useVoidDeposit } from '../../hooks/useDeposits';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { AttachmentsPanel } from '../../components/attachments/AttachmentsPanel';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/deposits/$id')({
  component: DepositDetailPage,
});

function DepositDetailPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: deposit, isLoading, error } = useDeposit(companyId, id);
  const clear = useSetDepositCleared(companyId);
  const voidDeposit = useVoidDeposit(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !deposit) return <p className="field-error">Failed to load deposit.</p>;

  const voidError =
    voidDeposit.error instanceof ApiError
      ? (voidDeposit.error.body as { error?: string } | null)?.error ?? 'Void failed'
      : voidDeposit.error?.message;

  return (
    <div>
      <Link to="/deposits" style={{ fontSize: 13 }}>← Deposit register</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Deposit {formatCurrency(deposit.amount)}
          {deposit.status === 'cleared' && <span className="tag ok" style={{ marginLeft: 8 }}>cleared</span>}
          {deposit.status === 'voided' && <span className="tag bad" style={{ marginLeft: 8 }}>voided</span>}
          {deposit.status === 'outstanding' && <span className="tag warn" style={{ marginLeft: 8 }}>outstanding</span>}
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {deposit.status === 'outstanding' && (
            <Link to="/deposits/$id/edit" params={{ id: deposit.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
              Edit
            </Link>
          )}
          <Link to="/deposits/new" search={{ copyFrom: deposit.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
            Copy
          </Link>
          {deposit.status !== 'voided' && (
            <>
              <button
                className="btn-secondary"
                disabled={clear.isPending}
                onClick={() => clear.mutate({ id: deposit.id, cleared: deposit.status !== 'cleared' })}
              >
                {deposit.status === 'cleared' ? 'Un-clear' : 'Mark cleared'}
              </button>
              <button
                className="btn-danger"
                disabled={voidDeposit.isPending}
                onClick={() => {
                  const reason = prompt('Reason for voiding this deposit?');
                  if (reason && reason.trim()) voidDeposit.mutate({ id: deposit.id, reason: reason.trim() });
                }}
              >
                Void
              </button>
            </>
          )}
        </div>
      </div>
      {voidError && <div className="field-error" style={{ marginBottom: 12 }}>{voidError}</div>}

      <div className="card" style={{ padding: 16, maxWidth: 760, marginBottom: 16 }}>
        <div className="form-grid" style={{ marginBottom: 8 }}>
          <span className="muted">Received from</span>
          <strong>{deposit.payerName}</strong>
          <span className="muted">Deposited to</span>
          <span>{deposit.bankAccountCode} — {deposit.bankAccountName}</span>
          <span className="muted">Date</span>
          <span>{formatAccountingDate(deposit.depositDate)}</span>
          {deposit.reference && (
            <>
              <span className="muted">Reference</span>
              <span>{deposit.reference}</span>
            </>
          )}
          {deposit.memo && (
            <>
              <span className="muted">Memo</span>
              <span>{deposit.memo}</span>
            </>
          )}
        </div>

        <h2 style={{ fontSize: 15, marginBottom: 8 }}>Allocation</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Memo</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {deposit.lines.map((l, i) => (
              <tr key={`${l.accountId}-${i}`}>
                <td>{l.accountCode} — {l.accountName}</td>
                <td className="muted">{l.memo ?? ''}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
              <td colSpan={2}>Total</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(deposit.amount)}</td>
            </tr>
          </tfoot>
        </table>
        {deposit.journalEntryId && (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            Posted as{' '}
            <Link to="/journal/$id" params={{ id: deposit.journalEntryId }}>journal entry</Link>.
            {deposit.status === 'voided' && deposit.voidReason && ` Voided: ${deposit.voidReason}.`}
          </p>
        )}
      </div>

      <AttachmentsPanel companyId={companyId} entityType="deposit" entityId={deposit.id} />
    </div>
  );
}
